"use client"

import { useEffect, useRef } from 'react'
import { create } from 'zustand'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { useStore } from '@/lib/store'
import { canOpen } from '@/lib/roles'
import { useFeature } from '@/hooks/use-feature'
import { createClient } from '@/lib/supabase/client'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { playChime, unlockChime } from '@/lib/chime'
import { ALERT_POLL_MS, alertMessage, announcedKey, arrivals, latestOnly, seenKey, seenMark, toAnnounce, unseenCount, type AlertOrder } from '@/lib/order-alerts'

/** The orders page reads its list again when an order arrives… */
export const ORDERS_ARRIVED = 'martepos:online-orders-arrived'
/** …and asks the alerts to look again after it moved one (the badge follows at once). */
export const ORDERS_CHECK = 'martepos:online-orders-check'

type Alerts = { open: AlertOrder[]; seenAt: string | null }
const useAlerts = create<Alerts>(() => ({ open: [], seenAt: null }))

/** The menu's badge: how many orders wait for an answer, and whether some came since this device last looked. */
export function useOrderAlertBadge(): { count: number; pulse: boolean } {
  const open = useAlerts((s) => s.open)
  const seenAt = useAlerts((s) => s.seenAt)
  const pathname = usePathname()
  return { count: open.length, pulse: !pathname.startsWith('/orders') && unseenCount(open, seenAt) > 0 }
}

function markSeen(orgId: string, open: AlertOrder[]) {
  const current = useAlerts.getState().seenAt
  const mark = seenMark(open, current)
  if (mark === current || mark === null) return
  useAlerts.setState({ seenAt: mark })
  try { localStorage.setItem(seenKey(orgId), mark) } catch { /* private mode: the mark lives for this visit */ }
}

/**
 * A new online order reaches the till by itself (lib/order-alerts.ts): every
 * 30 s — and when the window comes back — this asks for the unanswered
 * orders; one that was not there before chimes and says so, with „ნახვა".
 * Only where the shop's online store is on, for those who may open the orders.
 * Draws nothing; the menu reads `useOrderAlertBadge`.
 */
export function OrderAlerts() {
  const t = useT()
  const router = useRouter()
  const pathname = usePathname()
  const orgId = useStore((s) => s.currentOrg?.id ?? null)
  const hydrated = useStore((s) => s.isHydrated)
  const userRole = useStore((s) => s.userRole)
  const storeOn = useFeature('store')
  const enabled = !!orgId && hydrated && storeOn && !!userRole && canOpen('/orders', userRole)
  const onOrders = pathname.startsWith('/orders')
  const open = useAlerts((s) => s.open)
  // what the poll's closure must read as it is now, not as it was when it started
  const now = useRef({ t, router, onOrders })
  now.current = { t, router, onOrders }

  // the browser plays a page's sound only after a press on it: the first press wakes it
  useEffect(() => {
    if (!enabled) return
    const wake = () => unlockChime()
    window.addEventListener('pointerdown', wake, { capture: true })
    window.addEventListener('keydown', wake, { capture: true })
    return () => {
      window.removeEventListener('pointerdown', wake, { capture: true })
      window.removeEventListener('keydown', wake, { capture: true })
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled || !orgId) { useAlerts.setState({ open: [], seenAt: null }); return }
    let seenAt: string | null = null
    try { seenAt = localStorage.getItem(seenKey(orgId)) } catch { /* no storage: everything unanswered counts as unseen */ }
    useAlerts.setState({ open: [], seenAt })
    const supabase = createClient()
    let previous: Set<string> | null = null
    let live = true

    const ask = async () => supabase.from('online_orders')
      .select('id, number, total, created_at')
      .eq('status', 'new')
      .order('created_at', { ascending: false })
      .limit(100)
    // checks may overlap (the tick, the window coming back, the orders page): only the newest answer counts
    const check = latestOnly(ask, ({ data, error }) => {
      // offline or refused: keep what is shown, try again on the next tick
      if (!live || error || !data) return
      const list: AlertOrder[] = (data as { id: string; number: number; total: number | string; created_at: string }[]).map((r) => ({ id: r.id, number: r.number, total: Number(r.total), created_at: r.created_at }))
      const came = arrivals(previous, list)
      previous = new Set(list.map((o) => o.id))
      useAlerts.setState({ open: list })
      if (came.length === 0) return
      window.dispatchEvent(new Event(ORDERS_ARRIVED))
      // another tab of this device may have said so already: one chime, one message per order
      let said: string | null = null
      try { said = localStorage.getItem(announcedKey(orgId)) } catch { /* no storage: this tab speaks for itself */ }
      const { fresh, mark } = toAnnounce(came, said)
      if (fresh.length === 0) return
      if (mark) try { localStorage.setItem(announcedKey(orgId), mark) } catch { /* as above */ }
      const { t: tr, router: go, onOrders: here } = now.current
      playChime()
      const m = alertMessage(fresh)
      toast(tr(m.key, m.vars), {
        duration: 15_000,
        action: here ? undefined : { label: tr('oo.alert.view'), onClick: () => go.push('/orders') },
      })
    })

    void check()
    const tick = window.setInterval(() => void check(), ALERT_POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    const onAsk = () => void check()
    // the orders page seen in another tab of this device: the pulse stops here too
    const onStorage = (e: StorageEvent) => {
      if (e.key !== seenKey(orgId) || !e.newValue) return
      const current = useAlerts.getState().seenAt
      if (current === null || Date.parse(e.newValue) > Date.parse(current)) useAlerts.setState({ seenAt: e.newValue })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(ORDERS_CHECK, onAsk)
    window.addEventListener('storage', onStorage)
    return () => {
      live = false
      window.clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(ORDERS_CHECK, onAsk)
      window.removeEventListener('storage', onStorage)
    }
  }, [enabled, orgId])

  // the orders page in view: everything it shows is seen — the pulse stops, the count stays
  useEffect(() => {
    if (enabled && orgId && onOrders) markSeen(orgId, open)
  }, [enabled, orgId, onOrders, open])

  return null
}

/**
 * The unanswered count next to „ონლაინ შეკვეთები" (and on the phone's „მეტი").
 * It pulses only while an order came after this device last looked — a small
 * opacity/scale ring, like the green „სისტემა აქტიურია" dot; never under
 * reduced motion. The module's own blue, not red: red is for debts and deletes.
 */
export function OrderBadge({ count, pulse, className }: { count: number; pulse: boolean; className?: string }) {
  const t = useT()
  if (count === 0) return null
  return (
    <span data-order-badge data-pulse={pulse ? 'true' : 'false'} className={cn('relative inline-flex shrink-0', className)}>
      {pulse && <span aria-hidden className="absolute inset-0 rounded-full bg-sky-500/60 animate-ping motion-reduce:hidden" />}
      <span aria-hidden className="relative flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-500 px-1.5 text-[12px] font-bold leading-none text-white ring-2 ring-background">
        {count > 99 ? '99+' : count}
      </span>
      <span className="sr-only">{t('oo.alert.badge', { n: count })}</span>
    </span>
  )
}
